import React, { useState } from 'react';
import { isEmpty, isNil } from 'lodash';

import { useClaim } from '~/components/ClaimContainer';
import Button from '~/components/core/Atomic/Buttons/Button';
import PermissionsButtonWrapper from '~/components/core/Permissions/PermissionsButtonWrapper';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { useCms } from '~/components/hooks/useCms';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import Switch from '~/components/ui/atoms/Switch';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';
import { isClaimWriteDisabled } from '~/Utils/ClaimUtils';

import AttachDocumentRequestFileDialog from '../AttachDocumentRequestFileDialog';
import type { DocumentRequest } from '../documentRequestDisplay';
import RequestDocumentsLinkDialog from '../RequestDocumentsLinkDialog';
import { TEXTS } from '../texts';
import { useClaimDocumentRequests } from '../useDocumentRequests';

import DocumentRequestFilesTable, { type FileRow } from './DocumentRequestFilesTable';
import DocumentRequestLinksTable from './DocumentRequestLinksTable';

const toFileRows = (requests: DocumentRequest[]): FileRow[] =>
  requests.flatMap((request) =>
    (request.files ?? []).map((file) => ({
      ...file,
      id: file.uploaded_file_id,
      document_request_id: request.id,
      submitted_at: request.submitted_at ?? null,
    }))
  );

// The Documents screen's Requests tab.
const DocumentRequestsSection: React.FC = () => {
  const { userOrganization, user } = useCms();
  const isEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.DOCUMENT_REQUESTS);
  // The Documents tab itself has no permission gate, so without this a user denied CLAIM_DOCUMENT
  // would get the list route's 403 dialog on every visit.
  const canRead = useHasPermission({ action: PERMISSION_ACTIONS.CLAIM_DOCUMENT, verb: PERMISSION_VERBS.READ });
  const shouldShow = isEnabled && canRead;
  const { data, isLoading, isError, reloadData } = useClaimDocumentRequests({ shouldFetch: shouldShow });
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const [attachRow, setAttachRow] = useState<FileRow | null>(null);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [showAttached, setShowAttached] = useState(false);

  if (!shouldShow) {
    return null;
  }

  const requests = data?.document_requests;
  const fileRows = toFileRows(requests ?? []);
  const attachedCount = fileRows.filter((row) => !isNil(row.attached_document_id)).length;
  // The switch is not offered at 0, so a stale "on" must not keep the attached files and their column showing.
  // showAttached itself is kept: the server never clears an attachment, so a count of 0 comes only from an older
  // reply landing last, and the next reply brings the switch back with the adjuster's choice.
  const isShowingAttached = showAttached && attachedCount > 0;
  const visibleRows = isShowingAttached ? fileRows : fileRows.filter((row) => isNil(row.attached_document_id));
  // Shown only when no row is listed, which with files received means all of them are attached.
  const filesEmptyText = isEmpty(fileRows) ? TEXTS.FILES_EMPTY : TEXTS.FILES_ALL_ATTACHED;

  const body =
    isLoading || isError || isNil(requests) ? (
      <SkeletonTable rowsCount={3} columnsCount={5} isError={isError} />
    ) : (
      // Both tables always render, and each explains its own empty state.
      <div className="flex flex-col gap-12 pt-16">
        <div className="flex justify-end">
          {/* Styled as the Documents tab's "From Template" button. */}
          <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CLAIM_DOCUMENT} verb={PERMISSION_VERBS.WRITE}>
            <Button
              color="primary"
              disabled={isClaimWriteDisabled(claim, user)}
              onClick={() => setIsGenerateOpen(true)}
            >
              <AddIcon className="mr-4" />
              {TEXTS.NEW_LINK_BUTTON}
            </Button>
          </PermissionsButtonWrapper>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-8">
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
              {TEXTS.FILES_TITLE}
            </Text>
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {TEXTS.FILES_HINT}
            </Text>
          </div>
          {attachedCount > 0 && (
            <Switch
              checked={showAttached}
              onChange={(_event, checked) => setShowAttached(checked)}
              label={TEXTS.SHOW_ATTACHED_LABEL(attachedCount)}
            />
          )}
        </div>
        <DocumentRequestFilesTable
          rows={visibleRows}
          showAttachedColumn={isShowingAttached}
          emptyText={filesEmptyText}
          onReload={reloadData}
          onAttach={setAttachRow}
        />
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          {TEXTS.LINKS_TITLE}
        </Text>
        <DocumentRequestLinksTable requests={requests} onReload={reloadData} />
      </div>
    );

  return (
    <div>
      {body}
      {isGenerateOpen && (
        <RequestDocumentsLinkDialog
          claim={claim}
          onClose={async () => {
            setIsGenerateOpen(false);
            // The new link belongs in the Links table straight away.
            await reloadData();
          }}
        />
      )}
      {!isNil(attachRow) && (
        <AttachDocumentRequestFileDialog
          file={attachRow}
          onClose={() => setAttachRow(null)}
          onAttached={async () => {
            await reloadData();
            // The Documents table and the tab's document count read the claim, not this list.
            onAsyncClaimUpdate();
          }}
        />
      )}
    </div>
  );
};

export default DocumentRequestsSection;
